import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Heading, Text, SimpleGrid, Card, CardBody, Icon, Flex,
  Button, HStack, VStack, Badge, Spinner, Table, Thead, Tbody, Tr, Th, Td,
  Divider, IconButton, Tabs, TabList, Tab, Input, InputGroup, InputLeftElement,
  Select, Menu, MenuButton, MenuList, MenuItem, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
} from '@chakra-ui/react';
import { SearchIcon, DownloadIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  FaUsers, FaSyringe, FaClock, FaCheckCircle, FaPlus, FaUpload,
  FaTimes, FaUser, FaUserMd, FaShieldAlt, FaVial, FaInbox,
  FaTruck, FaHourglassHalf, FaFilter,
} from 'react-icons/fa';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { formatDistanceToNow } from 'date-fns';

const StatCard = ({ icon, label, value, color }) => (
  <Box bg="white" p={5} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100">
    <HStack spacing={4}>
      <Flex w={12} h={12} borderRadius="lg" bg={`${color}.50`} align="center" justify="center" flexShrink={0}>
        <Icon as={icon} boxSize={5} color={`${color}.500`} />
      </Flex>
      <Box>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight="1">{value}</Text>
        <Text fontSize="sm" color="gray.500" mt={0.5}>{label}</Text>
      </Box>
    </HStack>
  </Box>
);

const statusColor = (s) => {
  if (s === 'completed') return 'green';
  if (s === 'in_progress') return 'blue';
  if (s?.includes('waiting_on_lab')) return 'orange';
  if (s?.includes('waiting_to_be')) return 'purple';
  if (s === 'in_transit') return 'cyan';
  if (s === 'pending') return 'yellow';
  return 'gray';
};

export default function FacilityDashboard() {
  const { facility, facilityId } = useOutletContext();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedSub, setSelectedSub] = useState(null);
  const [subDetail, setSubDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { fetchSubmissions(); }, [facilityId]); // eslint-disable-line

  const fetchSubmissions = async () => {
    try {
      const { data } = await selectAll(() => supabase
        .from('submissions')
        .select('id, patient_name, patient_email, status, submitted_at, phlebotomist_name, doctor_name, blood_collection_time, draw_code')
        .eq('facility_id', facilityId)
        .order('submitted_at', { ascending: false }));
      setSubmissions(data || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const all = submissions;
    return {
      total: all.length,
      active: all.filter(s => s.status === 'in_progress' || s.status === 'pending').length,
      inTransit: all.filter(s => s.status === 'in_transit' || s.status === 'waiting_to_be_received').length,
      labResults: all.filter(s => s.status === 'waiting_on_lab_results').length,
      completed: all.filter(s => s.status === 'completed').length,
    };
  }, [submissions]);

  // Filtered by tab + search
  const filtered = useMemo(() => {
    let list = submissions;
    // Tab filter
    if (tabIndex === 1) list = list.filter(s => s.status === 'in_progress' || s.status === 'pending');
    if (tabIndex === 2) list = list.filter(s => s.status === 'in_transit' || s.status === 'waiting_to_be_received');
    if (tabIndex === 3) list = list.filter(s => s.status === 'completed');
    // Search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        (s.patient_name || '').toLowerCase().includes(q) ||
        (s.doctor_name || '').toLowerCase().includes(q) ||
        (s.draw_code || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [submissions, tabIndex, search]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const viewSubmission = async (sub) => {
    setSelectedSub(sub);
    setSubDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await supabase.from('submissions').select('*').eq('id', sub.id).single();
      // Only update if this is still the selected submission (prevents race condition)
      setSelectedSub(prev => prev?.id === sub.id ? prev : prev);
      if (sub.id) setSubDetail(data);
    } catch (err) { console.error('Detail error:', err); }
    finally { setDetailLoading(false); }
  };

  const exportCSV = () => {
    const headers = ['Patient', 'Doctor', 'Collected', 'Status', 'Draw Code'];
    const rows = filtered.map(s => [
      s.patient_name || '', s.doctor_name || '',
      s.blood_collection_time ? new Date(s.blood_collection_time).toLocaleDateString() : '',
      (s.status || 'pending').replace(/_/g, ' '),
      s.draw_code || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v.replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `facility_submissions.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <Box>
          <Text fontWeight="800" color="gray.800" fontSize="lg">{facility?.name || 'Facility Portal'}</Text>
        </Box>
        <HStack spacing={3}>
          <Button colorScheme="blue" leftIcon={<FaPlus />} size="sm" onClick={() => navigate(`/facility/${facilityId}/new-draw`)}>
            New Blood Draw
          </Button>
          <Button variant="outline" leftIcon={<FaUpload />} size="sm" onClick={() => navigate(`/facility/${facilityId}/bulk-upload`)}>
            Bulk Upload
          </Button>
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
        <StatCard icon={FaClipboardList} label="Total Submissions" value={loading ? '...' : stats.total} color="blue" />
        <StatCard icon={FaSyringe} label="In Progress" value={loading ? '...' : stats.active} color="purple" />
        <StatCard icon={FaTruck} label="In Transit" value={loading ? '...' : stats.inTransit} color="cyan" />
        <StatCard icon={FaHourglassHalf} label="Dropped at Lab" value={loading ? '...' : stats.labResults} color="teal" />
        <StatCard icon={FaCheckCircle} label="Completed" value={loading ? '...' : stats.completed} color="green" />
      </SimpleGrid>

      {/* Main Table */}
      <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden" border="1px solid" borderColor="gray.100">
        <Tabs index={tabIndex} onChange={(i) => { setTabIndex(i); setSearch(''); setCurrentPage(1); setSelectedSub(null); }} isLazy>
          <Box borderBottom="1px solid" borderColor="gray.100">
            <TabList border="none" px={2} pt={1}>
              <Tab py={3} px={4} fontSize="sm" fontWeight="medium" color="gray.500" _selected={{ color: 'blue.600', borderColor: 'blue.500', fontWeight: 'semibold' }}>
                <Icon as={FaInbox} mr={2} boxSize={3.5} />All ({stats.total})
              </Tab>
              <Tab py={3} px={4} fontSize="sm" fontWeight="medium" color="gray.500" _selected={{ color: 'purple.600', borderColor: 'purple.500', fontWeight: 'semibold' }}>
                <Icon as={FaSyringe} mr={2} boxSize={3.5} />Active ({stats.active})
              </Tab>
              <Tab py={3} px={4} fontSize="sm" fontWeight="medium" color="gray.500" _selected={{ color: 'cyan.600', borderColor: 'cyan.500', fontWeight: 'semibold' }}>
                <Icon as={FaTruck} mr={2} boxSize={3.5} />In Transit ({stats.inTransit})
              </Tab>
              <Tab py={3} px={4} fontSize="sm" fontWeight="medium" color="gray.500" _selected={{ color: 'green.600', borderColor: 'green.500', fontWeight: 'semibold' }}>
                <Icon as={FaCheckCircle} mr={2} boxSize={3.5} />Completed ({stats.completed})
              </Tab>
            </TabList>
          </Box>
        </Tabs>

        {/* Search + Controls */}
        <Flex px={4} py={3} gap={3} align="center" flexWrap="wrap">
          <InputGroup maxW="350px" size="sm">
            <InputLeftElement><SearchIcon color="gray.400" boxSize={3.5} /></InputLeftElement>
            <Input placeholder="Search patient, doctor, accession..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} />
          </InputGroup>
          <Flex flex={1} />
          <Menu>
            <MenuButton as={Button} size="sm" variant="outline" leftIcon={<DownloadIcon />} rightIcon={<ChevronDownIcon />}>Export All</MenuButton>
            <MenuList>
              <MenuItem onClick={exportCSV}>Export as CSV</MenuItem>
            </MenuList>
          </Menu>
          <Select size="sm" w="70px" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </Select>
        </Flex>

        {/* Table */}
        {loading ? (
          <Flex py={10} justify="center"><Spinner /></Flex>
        ) : paginated.length === 0 ? (
          <VStack py={10} spacing={3}>
            <Icon as={FaSyringe} boxSize={10} color="gray.300" />
            <Text color="gray.500">{search ? 'No submissions match your search' : 'No submissions yet'}</Text>
            {!search && <Button colorScheme="blue" size="sm" onClick={() => navigate(`/facility/${facilityId}/new-draw`)}>Create First Blood Draw</Button>}
          </VStack>
        ) : (
          <>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th py={3} fontSize="xs" color="gray.500">Patient</Th>
                    <Th py={3} fontSize="xs" color="gray.500">Doctor</Th>
                    <Th py={3} fontSize="xs" color="gray.500">Collected</Th>
                    <Th py={3} fontSize="xs" color="gray.500">Status</Th>
                    <Th py={3} fontSize="xs" color="gray.500">Results</Th>
                    <Th py={3} fontSize="xs" color="gray.500">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginated.map(sub => (
                    <Tr key={sub.id} _hover={{ bg: 'gray.50' }} bg={selectedSub?.id === sub.id ? 'blue.50' : undefined}>
                      <Td py={2.5}>
                        <Text fontWeight="500" fontSize="sm">{sub.patient_name || '—'}</Text>
                        <Text fontSize="xs" color="gray.400">{sub.draw_code}</Text>
                      </Td>
                      <Td py={2.5} fontSize="sm" color="gray.600">{sub.doctor_name || '—'}</Td>
                      <Td py={2.5} fontSize="sm" color="gray.600">
                        {sub.blood_collection_time ? new Date(sub.blood_collection_time).toLocaleDateString() : sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : '—'}
                      </Td>
                      <Td py={2.5}>
                        <Badge colorScheme={statusColor(sub.status)} fontSize="xs" textTransform="uppercase">
                          {(sub.status || 'pending').replace(/_/g, ' ')}
                        </Badge>
                      </Td>
                      <Td py={2.5} fontSize="sm" color="gray.500">
                        {sub.status === 'completed' ? <Text color="green.600" fontSize="xs" cursor="pointer">Download</Text> : <Text fontSize="xs" color="gray.400">Pending</Text>}
                      </Td>
                      <Td py={2.5}>
                        <Button size="xs" variant="outline" colorScheme="blue" onClick={() => viewSubmission(sub)}>
                          View
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {/* Detail Modal */}
            <Modal isOpen={!!selectedSub} onClose={() => { setSelectedSub(null); setSubDetail(null); setDetailLoading(false); }} size="xl" isCentered>
              <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
              <ModalContent borderRadius="2xl" mx={4}>
                <ModalHeader borderBottom="1px" borderColor="gray.100">
                  <HStack spacing={3}>
                    <Icon as={FaVial} color="blue.500" />
                    <Box>
                      <Text fontSize="md" fontWeight="700">Submission Details</Text>
                      {subDetail && <Text fontSize="xs" color="gray.500" fontWeight="400">Draw Code: {subDetail.draw_code || '—'}</Text>}
                    </Box>
                  </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody py={5}>
                  {detailLoading ? (
                    <Flex py={6} justify="center"><Spinner /></Flex>
                  ) : subDetail ? (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                      <Box>
                        <HStack mb={3}><Icon as={FaUser} color="blue.500" boxSize={4} /><Text fontWeight="700" fontSize="sm">Patient</Text></HStack>
                        <VStack align="start" spacing={1.5} pl={6} fontSize="sm" color="gray.700">
                          <Text><Text as="span" fontWeight="600" color="gray.500">Name:</Text> {subDetail.patient_name || '—'}</Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">DOB:</Text> {subDetail.patient_dob || '—'}</Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">Email:</Text> {subDetail.patient_email || '—'}</Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">Gender:</Text> {subDetail.patient_gender || '—'}</Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">Address:</Text> {subDetail.patient_address || '—'}</Text>
                        </VStack>
                      </Box>
                      <Box>
                        <HStack mb={3}><Icon as={FaUserMd} color="green.500" boxSize={4} /><Text fontWeight="700" fontSize="sm">Doctor</Text></HStack>
                        <VStack align="start" spacing={1.5} pl={6} fontSize="sm" color="gray.700">
                          <Text><Text as="span" fontWeight="600" color="gray.500">Name:</Text> {subDetail.doctor_name || '—'}</Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">Phone:</Text> {subDetail.doctor_phone || '—'}</Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">Email:</Text> {subDetail.doctor_email || '—'}</Text>
                        </VStack>
                      </Box>
                      <Box>
                        <HStack mb={3}><Icon as={FaShieldAlt} color="purple.500" boxSize={4} /><Text fontWeight="700" fontSize="sm">Insurance / Payment</Text></HStack>
                        <VStack align="start" spacing={1.5} pl={6} fontSize="sm" color="gray.700">
                          <Text><Text as="span" fontWeight="600" color="gray.500">Type:</Text> {subDetail.is_self_pay ? 'Self Pay' : 'Insurance'}</Text>
                          {!subDetail.is_self_pay && <>
                            <Text><Text as="span" fontWeight="600" color="gray.500">Company:</Text> {subDetail.insurance_company || '—'}</Text>
                            <Text><Text as="span" fontWeight="600" color="gray.500">Policy #:</Text> {subDetail.insurance_policy_number || '—'}</Text>
                          </>}
                        </VStack>
                      </Box>
                      <Box>
                        <HStack mb={3}><Icon as={FaVial} color="orange.500" boxSize={4} /><Text fontWeight="700" fontSize="sm">Submission Info</Text></HStack>
                        <VStack align="start" spacing={1.5} pl={6} fontSize="sm" color="gray.700">
                          <Text><Text as="span" fontWeight="600" color="gray.500">Status:</Text> <Badge colorScheme={statusColor(subDetail.status)}>{(subDetail.status || 'pending').replace(/_/g, ' ').toUpperCase()}</Badge></Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">Phlebotomist:</Text> {subDetail.phlebotomist_name || 'Not assigned'}</Text>
                          <Text><Text as="span" fontWeight="600" color="gray.500">Submitted:</Text> {subDetail.submitted_at ? new Date(subDetail.submitted_at).toLocaleString() : '—'}</Text>
                          {subDetail.special_instructions && <Text><Text as="span" fontWeight="600" color="gray.500">Instructions:</Text> {subDetail.special_instructions}</Text>}
                        </VStack>
                      </Box>
                    </SimpleGrid>
                  ) : (
                    <Text color="gray.500" fontSize="sm">Could not load details</Text>
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>

            {/* Pagination */}
            <Flex px={4} py={3} justify="space-between" align="center" borderTop="1px solid" borderColor="gray.100">
              <Text fontSize="sm" color="gray.500">
                Showing {Math.min((currentPage - 1) * pageSize + 1, filtered.length)}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
              </Text>
              <HStack spacing={1}>
                <Button size="xs" variant="ghost" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} isDisabled={currentPage === 1}>Prev</Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <Button key={page} size="xs" variant={currentPage === page ? 'solid' : 'ghost'} colorScheme={currentPage === page ? 'blue' : 'gray'} onClick={() => setCurrentPage(page)}>
                      {page}
                    </Button>
                  );
                })}
                <Button size="xs" variant="ghost" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>Next</Button>
              </HStack>
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
}

const FaClipboardList = (props) => (
  <svg {...props} viewBox="0 0 384 512" fill="currentColor"><path d="M336 64h-80c0-35.3-28.7-64-64-64s-64 28.7-64 64H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 40c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm144 418c0 3.3-2.7 6-6 6H54c-3.3 0-6-2.7-6-6V118c0-3.3 2.7-6 6-6h42v36c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12v-36h42c3.3 0 6 2.7 6 6z"/></svg>
);
